import type { PreOrderItemInput } from "./events";

/** Minimal shape of a basket line needed to describe a cart change. */
export interface BasketLine {
  readonly id: string;
  readonly name?: string;
  readonly quantity: number;
  readonly price?: number;
}

export interface CartChange {
  readonly event: "add_to_cart" | "remove_from_cart";
  readonly item: PreOrderItemInput;
}

/**
 * Describes a pre-order quantity change as a GA4 cart event.
 *
 * `amount` carries the change Collins applies to the line rather than its new
 * total: `/updateCustomerPreOrderItems` adds it to the quantity already in the
 * basket. The one exception is `0`, which clears the line, so it reports the
 * whole remaining quantity as a removal.
 *
 * @returns the change to track, or `null` when nothing moved.
 */
export function describeCartChange(
  basket: readonly BasketLine[],
  change: { itemId: string; amount: number; name?: string; price?: number },
): CartChange | null {
  const existing = basket.find((line) => line.id === change.itemId);
  const delta = change.amount === 0 ? -(existing?.quantity ?? 0) : change.amount;

  if (delta === 0) return null;

  return {
    event: delta > 0 ? "add_to_cart" : "remove_from_cart",
    item: {
      id: change.itemId,
      name: change.name ?? existing?.name ?? change.itemId,
      price: change.price ?? existing?.price,
      quantity: Math.abs(delta),
    },
  };
}
